import { useParams, Link } from 'react-router-dom';
import { getMovieById } from '@/mocks/movies';
import { articles } from '@/mocks/articles';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';

const articleSchema = (article: typeof articles[0]) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: article.title,
  description: article.excerpt,
  author: {
    '@type': 'Person',
    name: article.author,
  },
  datePublished: article.date,
  publisher: {
    '@type': 'Organization',
    name: '天堂影院',
    url: 'https://tiantangcinema.com',
  },
  url: `https://tiantangcinema.com/article/${article.id}`,
});

function ArticleContent({ id }: { id: string }) {
  const article = articles.find(a => a.id === id);

  if (!article) {
    return (
      <div className="text-center py-20">
        <p className="text-foreground-600">文章未找到</p>
        <Link to="/" className="text-primary-500 hover:text-primary-400 mt-4 inline-block text-sm">返回首页</Link>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={`天堂影院,${article.tags.join(',')},影评,电影资讯,高清在线观看`}
        canonicalUrl={`https://tiantangcinema.com/article/${article.id}`}
        ogType="article"
        schema={articleSchema(article)}
      />

      <main className="min-h-screen pt-16">
        <article>
          <div className="px-4 md:px-6 py-6 md:py-8">
            <div className="max-w-4xl mx-auto">
              {/* Breadcrumb */}
              <Breadcrumb items={[
                { label: article.category },
                { label: article.title },
              ]} />

              {/* Cover Image */}
              <div className="aspect-[2/1] rounded-lg overflow-hidden mb-6 md:mb-8">
                <img
                  src={article.coverUrl}
                  alt={article.title}
                  title={`${article.title} - 天堂影院`}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Header */}
              <header className="mb-6 md:mb-8">
                <div className="flex items-center gap-3 mb-3">
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-accent-100/70 text-accent-500">
                    {article.category}
                  </span>
                  <span className="text-xs text-foreground-600">{article.date}</span>
                  <span className="text-xs text-foreground-600">{article.views.toLocaleString()} 次阅读</span>
                </div>
                <h1 className="text-xl md:text-3xl font-bold font-heading text-foreground-50 mb-3">
                  {article.title}
                </h1>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {article.excerpt}
                </p>
                <div className="flex items-center gap-2 mt-3 text-xs text-foreground-700">
                  <span>作者：{article.author}</span>
                </div>
              </header>

              {/* Tags */}
              <div className="flex flex-wrap items-center gap-2 mb-6 md:mb-8">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full text-xs bg-secondary-100/70 text-secondary-900"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              {/* Content */}
              <div
                className="prose prose-invert max-w-none text-foreground-300 text-sm md:text-base leading-relaxed
                  [&>h3]:text-foreground-50 [&>h3]:font-bold [&>h3]:text-lg [&>h3]:mt-8 [&>h3]:mb-4
                  [&>p]:mb-4 [&>p]:leading-relaxed
                  [&>ul]:mb-4 [&>ul>li]:mb-1
                "
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Related Articles */}
              <div className="mt-12 md:mt-16 pt-8 border-t border-background-300/40">
                <h3 className="text-lg font-bold font-heading text-foreground-50 mb-5">相关文章</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {articles
                    .filter(a => a.id !== article.id)
                    .slice(0, 4)
                    .map((related) => (
                      <Link
                        key={related.id}
                        to={`/article/${related.id}`}
                        className="flex gap-3 bg-background-100 rounded-lg p-3 border border-background-300/40 hover:border-primary-500/20 transition-all duration-300 cursor-pointer group"
                      >
                        <div className="w-20 h-14 rounded overflow-hidden flex-shrink-0">
                          <img
                            src={related.coverUrl}
                            alt={related.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="min-w-0">
                          <h4 className="text-xs font-semibold text-foreground-300 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                            {related.title}
                          </h4>
                          <p className="text-xs text-foreground-600 mt-1">{related.date}</p>
                        </div>
                      </Link>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
    </>
  );
}

function MovieContent({ id }: { id: string }) {
  const movie = getMovieById(id);

  if (!movie) {
    return (
      <div className="text-center py-20">
        <p className="text-foreground-600">影片未找到</p>
        <Link to="/" className="text-primary-500 hover:text-primary-400 mt-4 inline-block text-sm">返回首页</Link>
      </div>
    );
  }

  const movieSchema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.description,
    dateCreated: `${movie.year}`,
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.cast.map(c => ({ '@type': 'Person', name: c })),
    countryOfOrigin: movie.region,
  };

  return (
    <>
      <SEOHead
        title={`${movie.title} - ${movie.titleEn} 高清在线观看`}
        description={`${movie.title}（${movie.titleEn}）${movie.year}年${movie.director}执导，${movie.cast.join('、')}主演。${movie.description.substring(0, 100)}... 天堂影院高清不缓冲在线观看，蓝光画质极速播放。`}
        keywords={`${movie.title},${movie.titleEn},${movie.category},高清在线观看,天堂影院,免费电影`}
        canonicalUrl={`https://tiantangcinema.com/article/${movie.id}`}
        ogType="video.movie"
        schema={movieSchema}
      />

      <main className="min-h-screen pt-16">
        <article>
          <div className="px-4 md:px-6 py-6 md:py-8">
            <div className="max-w-5xl mx-auto">
              {/* Breadcrumb */}
              <Breadcrumb items={[
                { label: '电影分类', to: '/category/all' },
                { label: movie.category, to: `/category/${movie.categorySlug}` },
                { label: movie.title },
              ]} />

              {/* Movie Detail */}
              <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
                {/* Poster */}
                <div className="w-full lg:w-72 flex-shrink-0">
                  <div className="aspect-[2/3] rounded-lg overflow-hidden">
                    <img
                      src={movie.posterUrl}
                      alt={movie.title}
                      title={`${movie.title} - 天堂影院高清在线观看`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1">
                  <header className="mb-4 md:mb-6">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-primary-100/70 text-primary-500">{movie.category}</span>
                      {movie.isHot && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-accent-100/70 text-accent-500">热门</span>
                      )}
                      {movie.isNew && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-accent-500 text-foreground-50">NEW</span>
                      )}
                    </div>
                    <h1 className="text-xl md:text-3xl font-bold font-heading text-foreground-50 mb-1">
                      {movie.title}
                    </h1>
                    <p className="text-sm text-foreground-600">{movie.titleEn}</p>
                  </header>

                  {/* Rating & Views */}
                  <div className="flex items-center gap-4 mb-5">
                    <div className="flex items-center gap-1">
                      <span className="text-2xl font-bold text-primary-500">{movie.rating}</span>
                      <span className="text-xs text-foreground-600">/ 10</span>
                    </div>
                    <div className="w-px h-5 bg-background-300/40"></div>
                    <span className="text-xs text-foreground-600">{movie.views.toLocaleString()} 次播放</span>
                  </div>

                  {/* Meta */}
                  <div className="grid grid-cols-2 gap-2 text-sm mb-5">
                    <div className="flex items-center gap-2">
                      <span className="text-foreground-600">导演：</span>
                      <span className="text-foreground-200">{movie.director}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-foreground-600">年份：</span>
                      <span className="text-foreground-200">{movie.year}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-foreground-600">地区：</span>
                      <span className="text-foreground-200">{movie.region}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-foreground-600">片长：</span>
                      <span className="text-foreground-200">{movie.duration}</span>
                    </div>
                  </div>

                  {/* Cast */}
                  <div className="mb-5">
                    <span className="text-sm text-foreground-600">主演：</span>
                    <span className="text-sm text-foreground-200">{movie.cast.join(' / ')}</span>
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap items-center gap-2 mb-5">
                    {movie.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1 rounded-full text-xs bg-secondary-100/70 text-secondary-900">
                        #{tag}
                      </span>
                    ))}
                  </div>

                  {/* Description */}
                  <div className="bg-background-100 rounded-lg p-4 md:p-5 border border-background-300/40 mb-5">
                    <h3 className="text-sm font-semibold text-foreground-200 mb-2">剧情简介</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{movie.description}</p>
                  </div>

                  {/* Watch & Share */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button className="flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-md text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-play-circle-fill"></i>
                      </div>
                      立即播放
                    </button>
                    <button className="flex items-center justify-center gap-2 bg-background-100 hover:bg-background-200 text-foreground-300 px-6 py-3 rounded-md text-sm font-medium transition-all duration-200 border border-background-300/40 cursor-pointer whitespace-nowrap">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-heart-line"></i>
                      </div>
                      收藏
                    </button>
                    <button className="flex items-center justify-center gap-2 bg-background-100 hover:bg-background-200 text-foreground-300 px-6 py-3 rounded-md text-sm font-medium transition-all duration-200 border border-background-300/40 cursor-pointer whitespace-nowrap">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-share-line"></i>
                      </div>
                      分享
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
    </>
  );
}

export default function ArticlePage() {
  const { id = '' } = useParams<{ id: string }>();

  // Check if it's an article or a movie
  const isArticle = articles.some(a => a.id === id);

  if (isArticle) {
    return <ArticleContent id={id} />;
  }

  return <MovieContent id={id} />;
}